import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// Referral System API hooks
export const useReferralStats = (userId?: number) => {
  return useQuery({
    queryKey: ["/api/admin/referrals/stats", userId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (userId) params.set("userId", userId.toString());
      
      const response = await apiRequest(`/api/admin/referrals/stats?${params}`, "GET");
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useReferralLeaderboard = (limit: number = 10) => {
  return useQuery({
    queryKey: ["/api/admin/referrals/leaderboard", limit],
    queryFn: async () => {
      const response = await apiRequest(`/api/admin/referrals/leaderboard?limit=${limit}`, "GET");
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useProcessReferralBonus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ referrerId, refereeId, bonusType }: ReferralBonusData) =>
      apiRequest("/api/admin/referrals/process-bonus", "POST", { referrerId, refereeId, bonusType }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics/referrals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({
        title: "Referral Bonus Processed",
        description: `Successfully processed ${variables.bonusType} bonus for referral.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Processing Failed",
        description: error.message || "Failed to process referral bonus.",
        variant: "destructive",
      });
    },
  });
};

export const useGenerateReferralCode = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (userId: number) =>
      apiRequest(`/api/admin/referrals/generate-code/${userId}`, "POST"),
// FIX: Cast data to any to access 'referralCode'
    onSuccess: (data: any, userId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users", userId] });
      toast({
        title: "Referral Code Generated",
        description: `Generated referral code: ${(data as any).referralCode}`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate referral code.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateReferralSettings = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (settings: ReferralSettings) => {
      // Update multiple settings
      const promises = Object.entries(settings).map(([key, value]) =>
        apiRequest(`/api/admin/settings/${key}`, "PUT", { value })
      );
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/referrals"] });
      toast({
        title: "Settings Updated",
        description: "Referral system settings have been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update referral settings.",
        variant: "destructive",
      });
    },
  });
};

// Get admin settings for referral configuration
export const useAdminSettings = () => {
  return useQuery({
    queryKey: ["/api/admin/settings"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/settings", "GET");
      return response.json();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useUpdateAdminSetting = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: any }) =>
      apiRequest(`/api/admin/settings/${key}`, "PUT", { value }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
      toast({
        title: "Setting Updated",
        description: `Successfully updated ${variables.key}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update setting.",
        variant: "destructive",
      });
    },
  });
};

// User management related hooks
export const useAllUsers = (filters?: any) => {
  return useQuery({
    queryKey: ["/api/admin/users", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters) {
        Object.keys(filters).forEach(key => {
          if (filters[key]) params.set(key, filters[key]);
        });
      }
      
      const response = await apiRequest(`/api/admin/users?${params}`, "GET");
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useAdminActivityLog = (filters?: any) => {
  return useQuery({
    queryKey: ["/api/admin/activity-log", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters) {
        Object.keys(filters).forEach(key => {
          if (filters[key]) params.set(key, filters[key]);
        });
      }
      
      const response = await apiRequest(`/api/admin/activity-log?${params}`, "GET");
      return response.json();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Referral system types
export interface ReferralBonusData {
  referrerId: number;
  refereeId: number;
  bonusType: "signup" | "first_purchase" | "milestone" | "special" | "vip";
}

export interface ReferralSettings {
  default_referrer_bonus?: string;
  default_referee_bonus?: string;
  referral_system_enabled?: string;
  max_referral_bonus?: string;
  milestone_bonus_5?: string;
  milestone_bonus_10?: string;
}

export interface ReferralStats {
  totalReferrals: number;
  activeReferrers: number;
  totalCreditsDistributed: number;
  averageReferrerBonus: number;
  conversionRate: number;
}

export interface ReferralLeaderboardEntry {
  name: string;
  email: string;
  totalReferrals: number;
  creditsEarned: number;
  referralsLast30d: number;
}